import React, { useState, useEffect, useCallback, useMemo } from "react";
import PageMeta from "../components/common/PageMeta";
import PageContainer from "../components/common/PageContainer";
import PageHeader from "../components/common/PageHeader";
import DisputeSummaryCards, { DisputeSummary, DisputeBucket, BucketSelection } from "../components/disputes/DisputeSummaryCards";
import DisputeActivity from "../components/disputes/DisputeActivity";
import DisputeDocuments from "../components/disputes/DisputeDocuments";
import DisputeStatusChip from "../components/disputes/DisputeStatusChip";
import DisputeEvidenceDrawerSection from "../components/disputes/DisputeEvidenceDrawerSection";
import { getTimeRemaining } from "../utils/timeRemaining";
import { formatFileSize } from "../utils/formatFileSize";
import { useApi } from "../hooks/useApi";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Box,
  Typography,
  Stack,
  Chip,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  Divider,
  ListItemText,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { HasPermission } from "../functions/auth";
import { PERMISSIONS } from "../constants/permissions";
import DataFilter, { PpAccountOption } from "../components/common/DataFilter";
import { PresetKey, presets } from "../components/common/dataFilterPresets";
import dayjs, { Dayjs } from "dayjs";
import { useSearchParams } from "react-router";
import { useCompanyId } from "../hooks/useCompanyId";
import { useBrandId } from "../hooks/useBrandId";
import { formatCurrency } from "../utils/formatCurrency";
import { adjustPpReason, pascalCase, ucFirstWord } from "../utils/textUtils";
import { formatPpAccountLabel } from "../utils/ppAccountLabel";
import { companySearchText } from "../utils/companyUtils";
import * as Sentry from "@sentry/react";
import LightTooltip from "../components/common/LightTooltip";
import InfoTooltip from "../components/common/InfoTooltip";
import PpLogo from "../components/common/PpLogo";
import DisputesCsvDialog from "../components/disputes/DisputesCsvDialog";
import { useStateValue } from "../context/StateValueContext";
import { usePpAccounts } from "../hooks/usePpAccounts";
import { Agent } from "../types/user";
import { DisputeCmStatus } from "../types/disputes";
import { EVIDENCE_PROVIDERS, type EvidenceProvider } from "../types/evidence";

// TypeScript interfaces
interface Dispute {
  dispute_id: number;
  company_name: string | null;
  brand_name: string | null;
  company_representment_enabled?: boolean;
  pp_account_name: string;
  status: DisputeCmStatus;
  assigned_user_id: number | null;
  assigned_first_name: string | null;
  assigned_email: string | null;
  pp_type: string;
  pp_dispute_id: string;
  source: string;
  pp_charge_id: string;
  pp_payment_id: string;
  pp_dispute_url: string;
  pp_amount: number;
  pp_currency: string;
  pp_reason: string;
  pp_status: string;
  pp_evidence_due_by: string;
  pp_evidence_sent_on?: string | null;
  pp_funds_withdrawn: boolean;
  pp_created_at: string;
  pp_updated_at: string;
  pp_closed_at: string | null;
  pp_payment_method: string;
  isRdr?: boolean;
  isShopifyProtected?: boolean;
  covered_by_paypal?: boolean;
  outcome_label?: 'reversed' | 'inferred' | 'defended' | 'escalated' | null;
  pp_case_type?: string;
  dispute_channel?: string;
  life_cycle_stage?: string; // only paypal
  paypal_outcome_code?: string; // only paypal
  paypal_outcome_reason?: string; // only paypal
  card_number?: string;
  gw_auth_code?: string;
  gw_transaction_id?: string;
  pp_transaction_date?: string;
  order_id?: string;
  order_name?: string;
  client_ip?: string;
  cm_notes?: string | null;
}

export interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface Filters {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  pp_status?: string;
  companyId?: string;
}

interface DisputesResponse {
  disputes: Dispute[];
  pagination: Pagination;
  filters: Filters;
  summary?: DisputeSummary;
  serverNow?: string;
}

interface RefreshDisputeResponse {
  syncStatus: 'noop' | 'created' | 'updated';
}
const statusOptions = [
  { value: 'needs_response', label: 'Needs Response' },
  { value: 'waiting_for_seller_response', label: 'Waiting for Seller Response' }, // paypal
  { value: 'under_review', label: 'Under Review' },
  { value: 'waiting_for_buyer_response', label: 'Waiting for Buyer Response' }, // paypal
  { value: 'cancelled', label: 'Cancelled' }, // paypal
  { value: 'resolved', label: 'Resolved' }, // paypal
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'refunded', label: 'Refunded' }, // paypal
  { value: 'partially_refunded', label: 'Partially Refunded' }, // paypal
  { value: 'accepted', label: 'Accepted' },
  { value: 'appealable', label: 'Appealable' }, // paypal
  { value: 'closed', label: 'Closed' },
  { value: 'undefended', label: 'Undefended' }, // adyen
  { value: 'unresponded', label: 'Unresponded' }, // adyen
  { value: 'pending', label: 'Pending' }, // adyen
  { value: 'responded', label: 'Responded' }, // adyen
  { value: 'expired', label: 'Expired' }, // adyen
];

const cmStatusOptions = Object.values(DisputeCmStatus).map(value => ({ value, label: pascalCase(value) }));

const splitParam = (value: string | null): string[] =>
  value ? value.split(',').filter(Boolean) : [];


const formatDateTime = (dateString: string) => {
  return dayjs.utc(dateString).format('MMM DD HH:mm');
};

const formatDateTimeWithTimezone = (dateString: string) => {
  return dayjs.utc(dateString).format('MMM DD HH:mm [UTC]');
};

const formatDate = (dateString: string) => {
  return dayjs.utc(dateString).format('MMM DD');
};
// The evidence-due countdown shows for any status that can still carry a live
// deadline, including UNKNOWN.
const evidenceDueVisibleStatuses: DisputeCmStatus[] = [
  DisputeCmStatus.OPEN,
  DisputeCmStatus.IN_PROGRESS,
  DisputeCmStatus.UNDER_REVIEW,
  DisputeCmStatus.UNKNOWN,
];

// The red "expired" highlight must mirror the API's AWAITING_RESPONSE_STATUSES
// (alertQueryFilters.ts) so highlighted rows agree with the server-computed
// queue-health cards and bucket drill-down. UNDER_REVIEW (evidence sent) and
// UNKNOWN are excluded — the cards don't count them as expired.
const awaitingResponseStatuses: DisputeCmStatus[] = [
  DisputeCmStatus.OPEN,
  DisputeCmStatus.IN_PROGRESS,
];

const BUCKET_PARAM: Record<DisputeBucket, string> = {
  overdue: 'overdue',
  dueIn3Days: 'due_3d',
  dueIn4To7Days: 'due_4_7d',
};

const bucketFromParams = (params: URLSearchParams): BucketSelection | null => {
  const bucket = (Object.keys(BUCKET_PARAM) as DisputeBucket[]).find((k) => BUCKET_PARAM[k] === params.get("bucket"));
  return bucket ? { bucket, unassignedOnly: params.get("unassigned") === "1" } : null;
};
// Shopify auto-submits evidence for the merchant once the response deadline lapses, so an
// evidence_sent_on stamped after evidence_due_by means the submission wasn't ours.
const SECONDARY_STATUS_CHIP_SX = { fontWeight: 500, borderRadius: '10px' };
const OUTCOME_LABEL_CHIP_SX = { height: 22, fontSize: 12, fontWeight: 500, color: '#667085', borderColor: '#D0D5DD', cursor: 'default' };
const OUTCOME_LABELS = {
  reversed: {
    label: 'Reversed',
    tooltip: 'The money is back with the merchant, but the card issuer can still object for a few more weeks. It counts as won once that time passes quietly.',
  },
  inferred: {
    label: 'Inferred',
    tooltip: "Adyen never sent a final result for this dispute. The issuer stayed silent past the deadline, so the outcome follows Adyen's own rule: money returned means won, money kept means lost.",
  },
  defended: {
    label: 'Defended',
    tooltip: (
      <>
        <div>The dispute was defended and the money is still with the issuer. Nothing more can be submitted. Money returned means won; silence past the deadline means lost.</div>
        <div style={{ marginTop: 8 }}>Adyen may already show this as lost while it is pending here. Adyen gives up on the issuer sooner; we wait out the full window.</div>
      </>
    ),
  },
  escalated: {
    label: 'Escalated',
    tooltip: 'The issuer rejected the defence and the case has moved to pre-arbitration or arbitration. The card scheme now rules on it and sends a final won or lost result.',
  },
} as const;

const isAutoSubmitted = (dispute: Dispute): boolean =>
  !!dispute.pp_evidence_sent_on
  && !!dispute.pp_evidence_due_by
  && dayjs.utc(dispute.pp_evidence_sent_on).isAfter(dayjs.utc(dispute.pp_evidence_due_by));

const isDisputeAssignable = (dispute: Dispute) => {
  return !dispute.isRdr && !dispute.isShopifyProtected;
};

// max-width is inert on a table cell under the automatic table layout, so a column
// only stays bounded if the clamp sits on a block child of the cell.
const clampStyle = (maxWidth: number): React.CSSProperties => ({
  maxWidth,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

// Column configuration
const COLUMNS_STORAGE_KEY = 'disputes_visible_columns_v2';

interface ColumnConfig {
  id: string;
  label: string;
  sortField?: string;
  isDefault: boolean;
}

const allColumns: ColumnConfig[] = [
  { id: 'account', label: 'Account', isDefault: true },
  { id: 'company_name', label: 'Company', isDefault: false },
  { id: 'brand_name', label: 'Brand', isDefault: false },
  { id: 'case_type', label: 'Case Type', isDefault: true },
  { id: 'reason', label: 'Reason', isDefault: true },
  { id: 'pp_status', label: 'PP Status', isDefault: true },
  { id: 'status', label: 'Status', isDefault: true },
  { id: 'amount', label: 'Amount', isDefault: true },
  { id: 'created', label: 'Created', sortField: 'pp_created_at', isDefault: true },
  { id: 'evidence_due', label: 'Evidence Due', sortField: 'evidence_due_by', isDefault: true },
  { id: 'rdr', label: 'RDR', isDefault: true },
  // Additional columns
  { id: 'shopify_protected', label: 'Shopify Protected', isDefault: false },
  { id: 'order_id', label: 'Order ID', isDefault: false },
  { id: 'order_name', label: 'Order Name', isDefault: false },
  { id: 'dispute_id', label: 'Dispute ID', isDefault: false },
  { id: 'transaction_id', label: 'Transaction ID', isDefault: false },
  { id: 'payment_method', label: 'Payment Method', isDefault: false },
  { id: 'card_number', label: 'Card Number', isDefault: false },
  { id: 'gw_auth_code', label: 'Gateway Auth Code', isDefault: false },
  { id: 'gw_transaction_id', label: 'Gateway Transaction ID', isDefault: false },
  { id: 'client_ip', label: 'Client IP', isDefault: false },
  { id: 'representment', label: 'Representment', isDefault: false },
  { id: 'cm_notes', label: 'Notes', isDefault: false },
];

const defaultColumnIds = allColumns.filter(c => c.isDefault).map(c => c.id);
const validColumnIds = allColumns.map(c => c.id);

const getStoredColumns = (): string[] => {
  try {
    const stored = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Filter to only valid column IDs
        return parsed.filter((id: string) => validColumnIds.includes(id));
      }
    }
  } catch (e) {
    console.error('Failed to parse stored columns', e);
  }
  return defaultColumnIds;
};

const getPpAccountKey = (ppType: string, ppAccountId: number | string) => `${ppType}:${ppAccountId}`;

// Providers that have a dispute table/query branch. Others (e.g. Checkout Champ)
// can't filter disputes, so they're excluded from the PP Account filter.
const DISPUTE_PP_TYPES: string[] = ['stripe', 'shopify', 'paypal', 'adyen'];

// Mirrors ALLOWED_DOCUMENT_EXTENSIONS in apps/api/services/disputeDocumentService.ts
// (the API is the enforcing side; keep the two lists in sync).
const DOCUMENT_ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt,.eml,.msg';

const saveStoredColumns = (columnIds: string[]) => {
  localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columnIds));
};

function DisputeRow({ dispute, visibleColumns, isSelected, onToggleSelect, selectionEnabled, showActivity, canWrite, canManageNotes, isAgencyView, serverNow, onRefreshed, onNotify }: {
  dispute: Dispute,
  visibleColumns: string[],
  isSelected: boolean,
  onToggleSelect: (disputeId: number) => void,
  selectionEnabled: boolean,
  showActivity: boolean,
  canWrite: boolean,
  canManageNotes: boolean,
  isAgencyView: boolean,
  serverNow?: string,
  onRefreshed: () => void,
  onNotify: (message: string, severity: 'success' | 'error') => void,
}
) {
  const [open, setOpen] = React.useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [refreshing, setRefreshing] = React.useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = React.useState(false);
  const [editNotes, setEditNotes] = React.useState('');
  const [savingNotes, setSavingNotes] = React.useState(false);
  const [notesError, setNotesError] = React.useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
  const [uploadFile, setUploadFile] = React.useState<File | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [docsVersion, setDocsVersion] = React.useState(0);
  const menuOpen = Boolean(menuAnchorEl);
  const { post, patch, postForm } = useApi();

  // Highlight red only for the statuses the server counts as active (a subset of
  // the countdown-visible set, so a red row always has a visible deadline), and
  // decide "expired" against the server's clock so it agrees with the queue cards.
  // Clients never see the expired treatment (red row + "expired" text) — per Migle,
  // an already-lapsed deadline is agency-facing only, so it's gated on isAgencyView.
  const expiredReference = serverNow ? dayjs.utc(serverNow) : dayjs.utc();
  const evidenceExpired = isAgencyView
    && awaitingResponseStatuses.includes(dispute.status)
    && !!dispute.pp_evidence_due_by
    && dayjs.utc(dispute.pp_evidence_due_by).isBefore(expiredReference);

  // Adyen is webhook-only: there's no dispute-fetch API for Refresh to call.
  const isAdyen = dispute.pp_type === 'adyen';

  // The actions menu holds "Open in <provider>" (needs a provider URL), "Refresh"
  // (needs write access) and "Notes" (agency-only). Hide the ⋯ when none apply.
  const hasRowActions = !!dispute.pp_dispute_url || canWrite || canManageNotes;

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };
  const handleCloseMenu = () => setMenuAnchorEl(null);

  const handleOpenInPp = () => {
    handleCloseMenu();
    if (dispute.pp_dispute_url) {
      window.open(dispute.pp_dispute_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOpenNotesDialog = () => {
    handleCloseMenu();
    setEditNotes(dispute.cm_notes ?? '');
    setNotesError(null);
    setNotesDialogOpen(true);
  };

  const handleCloseNotesDialog = () => {
    if (savingNotes) return;
    setNotesDialogOpen(false);
  };

  const handleNotesSubmit = async () => {
    if (!editNotes.trim() || savingNotes) return;
    setSavingNotes(true);
    setNotesError(null);
    try {
      await patch(`/api/disputes/${dispute.dispute_id}/notes`, { notes: editNotes.trim() });
      setNotesDialogOpen(false);
      onNotify('Notes saved', 'success');
      onRefreshed();
    } catch (err: any) {
      Sentry.captureException(err);
      setNotesError(err?.message || 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleOpenUploadDialog = () => {
    handleCloseMenu();
    setUploadFile(null);
    setUploadError(null);
    setUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    if (uploading) return;
    setUploadDialogOpen(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    // reset so picking the same file again re-triggers onChange
    e.target.value = '';
    if (file && file.size > 25 * 1024 * 1024) {
      setUploadError('File exceeds the 25 MB limit');
      return;
    }
    setUploadError(null);
    setUploadFile(file);
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile || uploading) return;
    setUploading(true);
    setUploadError(null);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      await postForm(`/api/disputes/${dispute.dispute_id}/documents`, fd, { slowApi: true });
      setUploadDialogOpen(false);
      onNotify('Document uploaded', 'success');
      setDocsVersion(v => v + 1);
    } catch (err: any) {
      Sentry.captureException(err);
      setUploadError(err?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleRefresh = async () => {
    handleCloseMenu();
    setRefreshing(true);
    try {
      const res = await post<RefreshDisputeResponse>(`/api/disputes/${dispute.dispute_id}/refresh`, {});
      const status = res?.syncStatus ?? 'noop';
      const statusLabels: Record<RefreshDisputeResponse['syncStatus'], string> = {
        noop: 'No changes found',
        updated: 'Updated',
        created: 'Inserted',
      };
      onNotify(`Dispute refreshed (${statusLabels[status]})`, 'success');
      if (status !== 'noop') {
        onRefreshed();
      }
    } catch (err: any) {
      Sentry.captureException(err);
      onNotify(err?.message || 'Failed to refresh dispute', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const renderCell = (columnId: string) => {
    switch (columnId) {
      case 'account':
        return (
          <TableCell key={columnId}>
            {dispute.pp_account_name}
          </TableCell>
        );
      case 'case_type':
        return (
          <TableCell key={columnId}>
            <div style={clampStyle(200)}>{dispute.pp_case_type ? pascalCase(dispute.pp_case_type) : '--'}</div>
          </TableCell>
        );
      case 'reason':
        return (
          <TableCell key={columnId}>
            <div style={clampStyle(200)}>{dispute.pp_reason ? adjustPpReason(dispute.pp_reason) : '--'}</div>
          </TableCell>
        );
      case 'pp_status':
        return (
          <TableCell key={columnId}>
            <div style={clampStyle(200)}>{pascalCase(dispute.pp_status.replace('warning_', '').toLowerCase())}</div>
          </TableCell>
        );
      case 'amount':
        return (
          <TableCell key={columnId}>
            {formatCurrency(dispute.pp_amount, dispute.pp_currency)} {dispute.pp_currency.toUpperCase()}
          </TableCell>
        );
      case 'created':
        return (
          <TableCell key={columnId}>
            <LightTooltip title={formatDateTime(dispute.pp_created_at)} placement='top-end'>
              <div>{formatDate(dispute.pp_created_at)}</div>
            </LightTooltip>
          </TableCell>
        );
      case 'evidence_due': {
        const showEvidenceDue = evidenceDueVisibleStatuses.includes(dispute.status) && !!dispute.pp_evidence_due_by;
        // Only agencies see "expired" wording, and only while a response is still owed —
        // otherwise a lapsed deadline shows the plain due date instead.
        const isPastDue = showEvidenceDue && dayjs.utc(dispute.pp_evidence_due_by).isBefore(expiredReference);
        const dueText = (!isAgencyView || !awaitingResponseStatuses.includes(dispute.status)) && isPastDue
          ? formatDate(dispute.pp_evidence_due_by)
          : getTimeRemaining(dispute.pp_evidence_due_by, expiredReference);
        return (
          <TableCell key={columnId}>
            {showEvidenceDue ? (
              <LightTooltip title={formatDateTime(dispute.pp_evidence_due_by)} placement='top-end'>
                <div style={evidenceExpired ? { color: '#d92d20', fontWeight: 600 } : undefined}>{dueText}</div>
              </LightTooltip>
            ) : (
              <div>--</div>
            )}
          </TableCell>
        );
      }
      case 'rdr':
        return <TableCell key={columnId}>{dispute.isRdr ? 'Yes' : 'No'}</TableCell>;
      case 'status':
        return (
          <TableCell key={columnId}>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              <DisputeStatusChip status={dispute.status} />
              {isAgencyView && isAutoSubmitted(dispute) && (
                <Chip label="Auto-submitted" sx={{ ...SECONDARY_STATUS_CHIP_SX, backgroundColor: '#FFF1F1', color: '#d92d20' }} />
              )}
              {dispute.covered_by_paypal && (
                <Chip label="Covered by PayPal" sx={{ ...SECONDARY_STATUS_CHIP_SX, backgroundColor: '#F2F4F7', color: '#344054' }} />
              )}
              {isAgencyView && dispute.outcome_label && (
                <InfoTooltip title={OUTCOME_LABELS[dispute.outcome_label].tooltip} placement="top">
                  <Chip label={OUTCOME_LABELS[dispute.outcome_label].label} size="small" variant="outlined" sx={OUTCOME_LABEL_CHIP_SX} />
                </InfoTooltip>
              )}
            </div>
          </TableCell>
        );
      case 'company_name':
        return <TableCell key={columnId}>{dispute.company_name || '--'}</TableCell>;
      case 'brand_name':
        return <TableCell key={columnId}>{dispute.brand_name || '--'}</TableCell>;
      case 'shopify_protected':
        return <TableCell key={columnId}>{dispute.isShopifyProtected ? 'Yes' : 'No'}</TableCell>;
      case 'order_id':
        return <TableCell key={columnId}>{dispute.order_id || '--'}</TableCell>;
      case 'order_name':
        return <TableCell key={columnId}>{dispute.order_name || '--'}</TableCell>;
      case 'dispute_id':
        return <TableCell key={columnId}>{dispute.pp_dispute_id || '--'}</TableCell>;
      case 'transaction_id':
        return <TableCell key={columnId}>{dispute.pp_charge_id || '--'}</TableCell>;
      case 'payment_method':
        return <TableCell key={columnId}>{pascalCase(dispute.pp_payment_method) || '--'}</TableCell>;
      case 'card_number':
        return <TableCell key={columnId}>{dispute.card_number || '--'}</TableCell>;
      case 'gw_auth_code':
        return <TableCell key={columnId}>{dispute.gw_auth_code || '--'}</TableCell>;
      case 'gw_transaction_id':
        return <TableCell key={columnId}>{dispute.gw_transaction_id || '--'}</TableCell>;
      case 'client_ip':
        return <TableCell key={columnId}>{dispute.client_ip || '--'}</TableCell>;
      case 'representment':
        return <TableCell key={columnId}>{dispute.company_representment_enabled === false ? 'No' : 'Yes'}</TableCell>;
      case 'cm_notes':
        return (
          <TableCell key={columnId}>
            {dispute.cm_notes ? (
              <LightTooltip title={dispute.cm_notes} placement='top-end'>
                <div style={clampStyle(240)}>{dispute.cm_notes}</div>
              </LightTooltip>
            ) : '--'}
          </TableCell>
        );
      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <TableRow key={dispute.dispute_id} hover onClick={() => setOpen(!open)} sx={{
        '& > *': { borderBottom: 'unset' },
        cursor: 'pointer',
        ...(evidenceExpired && {
          backgroundColor: 'rgba(217, 45, 32, 0.06)',
          '&.MuiTableRow-hover:hover': { backgroundColor: 'rgba(217, 45, 32, 0.12)' },
          '& > *:first-of-type': { boxShadow: 'inset 3px 0 0 #d92d20' },
        }),
      }}>
        {selectionEnabled && (
          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isSelected}
              onChange={() => onToggleSelect(dispute.dispute_id)}
              disabled={!isDisputeAssignable(dispute)}
              size="small"
            />
          </TableCell>
        )}
        <TableCell>
          <PpLogo ppType={dispute.pp_type} />
        </TableCell>
        {allColumns.filter(c => visibleColumns.includes(c.id)).map(c => renderCell(c.id))}
        <HasPermission permission={PERMISSIONS.DISPUTES_ALL_COMPANIES}>
          <TableCell>{dispute.assigned_user_id ? (
            <LightTooltip
              title={dispute.assigned_email}
              placement='top-end'>
              <div>
                {dispute.assigned_first_name}
              </div>
            </LightTooltip>
          ) : (
            '--'
          )}</TableCell>
        </HasPermission>
        <TableCell onClick={(e) => e.stopPropagation()}>
          {hasRowActions && (
            <>
          <IconButton
            aria-label="more actions"
            aria-controls={menuOpen ? `dispute-menu-${dispute.dispute_id}` : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? 'true' : undefined}
            onClick={handleOpenMenu}
            size="small"
          >
            {refreshing ? <CircularProgress size={18} /> : <MoreVertIcon fontSize="small" />}
          </IconButton>
          <Menu
            id={`dispute-menu-${dispute.dispute_id}`}
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {dispute.pp_dispute_url && (
              <MenuItem onClick={handleOpenInPp}>
                <ListItemText>Open in {pascalCase(dispute.pp_type)}</ListItemText>
                <OpenInNewIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
              </MenuItem>
            )}
            {!isAdyen && (
              <HasPermission permission={PERMISSIONS.DISPUTE_WRITE}>
                <MenuItem onClick={handleRefresh} disabled={refreshing}>
                  <ListItemText>Refresh</ListItemText>
                </MenuItem>
              </HasPermission>
            )}
            {canManageNotes && (
              <>
                <MenuItem onClick={handleOpenNotesDialog}>
                  <ListItemText>{dispute.cm_notes ? 'Edit Notes' : 'Add Notes'}</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleOpenUploadDialog}>
                  <ListItemText>Upload Document</ListItemText>
                </MenuItem>
              </>
            )}
          </Menu>
            </>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={100}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                Dispute Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                <HasPermission permission={PERMISSIONS.DISPUTES_ALL_COMPANIES}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Company Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {dispute.company_name}
                    </Typography>
                  </Box>
                </HasPermission>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Account Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {dispute.pp_account_name}
                  </Typography>
                </Box>
                {dispute.order_id && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Order ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {dispute.order_id}
                    </Typography>
                  </Box>
                )}
                {dispute.order_name && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Order Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {dispute.order_name}
                    </Typography>
                  </Box>
                )}
                {dispute.dispute_channel && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Dispute Channel
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {pascalCase(dispute.dispute_channel)}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Dispute ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {dispute.pp_dispute_id}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Transaction ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {dispute.pp_charge_id || '--'}
                  </Typography>
                </Box>
                {dispute.pp_transaction_date && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Transaction Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDateTimeWithTimezone(dispute.pp_transaction_date)}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Dispute Reason
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {dispute.pp_reason ? ucFirstWord(dispute.pp_reason) : '--'}
                  </Typography>
                </Box>
                {dispute.life_cycle_stage && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Life Cycle Stage
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {pascalCase(dispute.life_cycle_stage)}
                    </Typography>
                  </Box>
                )}
                {dispute.paypal_outcome_code && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      PayPal Outcome Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {pascalCase(dispute.paypal_outcome_code)}
                    </Typography>
                  </Box>
                )}
                {dispute.paypal_outcome_reason && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      PayPal Outcome Reason
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {pascalCase(dispute.paypal_outcome_reason)}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Payment Method
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {pascalCase(dispute.pp_payment_method) || '--'}
                  </Typography>
                </Box>
                {dispute.card_number && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Card Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {dispute.card_number}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Funds Withdrawn
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {dispute.pp_funds_withdrawn ? 'Yes' : 'No'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    is RDR
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {dispute.isRdr ? 'Yes' : 'No'}
                  </Typography>
                </Box>
                {dispute.pp_type === 'shopify' && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      is Shopify Protected
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {dispute.isShopifyProtected ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                )}
                {dispute.gw_auth_code && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Payment Gateway Auth Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {dispute.gw_auth_code}
                    </Typography>
                  </Box>
                )}
                {dispute.gw_transaction_id && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Payment Gateway Transaction ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {dispute.gw_transaction_id}
                    </Typography>
                  </Box>
                )}
                {dispute.client_ip && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Client IP
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {dispute.client_ip}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {dispute.pp_created_at ? formatDateTimeWithTimezone(dispute.pp_created_at) : '--'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Updated At
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {dispute.pp_updated_at ? formatDateTimeWithTimezone(dispute.pp_updated_at) : '--'}
                  </Typography>
                </Box>
                {dispute.pp_closed_at && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Closed At
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {dispute.pp_closed_at ? formatDateTimeWithTimezone(dispute.pp_closed_at) : '--'}
                    </Typography>
                  </Box>
                )}
              </Box>
              {EVIDENCE_PROVIDERS.includes(dispute.pp_type as EvidenceProvider) && (
                <HasPermission permission={PERMISSIONS.DISPUTE_EVIDENCE}>
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 600 }}>
                      Evidence
                    </Typography>
                    <DisputeEvidenceDrawerSection disputeId={dispute.dispute_id} open={open} />
                  </Box>
                </HasPermission>
              )}
              {canManageNotes && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 600 }}>
                    Documents
                  </Typography>
                  <DisputeDocuments disputeId={dispute.dispute_id} open={open} version={docsVersion} onNotify={onNotify} />
                </Box>
              )}
              {showActivity && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 600 }}>
                    Activity
                  </Typography>
                  <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                    <DisputeActivity disputeId={dispute.dispute_id} open={open} version={docsVersion} />
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onClose={handleCloseNotesDialog} fullWidth maxWidth="lg" aria-labelledby="dispute-notes-dialog-title">
        <DialogTitle id="dispute-notes-dialog-title">
          {dispute.cm_notes ? 'Edit Notes' : 'Add Notes'} — Dispute #{dispute.dispute_id}
        </DialogTitle>
        <DialogContent>
          {notesError && (
            <Alert severity="error" sx={{ mb: 2 }}>{notesError}</Alert>
          )}
          <TextField
            fullWidth
            label="Notes"
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            multiline
            minRows={10}
            disabled={savingNotes}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseNotesDialog} color="inherit" disabled={savingNotes}>Cancel</Button>
          <Button
            onClick={handleNotesSubmit}
            variant="contained"
            color="primary"
            disabled={!editNotes.trim() || savingNotes}
            startIcon={savingNotes ? <CircularProgress size={16} /> : null}
          >
            {savingNotes ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Upload Document Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} aria-labelledby="dispute-upload-dialog-title">
        <DialogTitle id="dispute-upload-dialog-title">
          Upload Document — Dispute #{dispute.dispute_id}
        </DialogTitle>
        <DialogContent>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>
          )}
          <Button component="label" variant="outlined" disabled={uploading} sx={{ mt: 1 }}>
            Choose file
            <input type="file" hidden accept={DOCUMENT_ACCEPT} onChange={handleFileChange} />
          </Button>
          {uploadFile && (
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              {uploadFile.name} ({formatFileSize(uploadFile.size)})
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseUploadDialog} color="inherit" disabled={uploading}>Cancel</Button>
          <Button
            onClick={handleUploadSubmit}
            variant="contained"
            color="primary"
            disabled={!uploadFile || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : null}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

export default function Disputes() {
  const { get, post, error: apiError } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const { companyId } = useCompanyId();
  const { brandId } = useBrandId();
  const [{ user, agents, miniCompanies }, dispatch] = useStateValue();
  const { ppAccounts } = usePpAccounts();

  // range=all (dashboard state-now cards deep-link here) = no date window at all.
  const rangeParam = searchParams.get("range");
  const isAllTimeRange = rangeParam === "all";
  const initialPreset = isAllTimeRange ? "custom" : (rangeParam as PresetKey) || "30D";
  const initialStart = isAllTimeRange ? null : searchParams.get("start") ? dayjs.utc(searchParams.get("start")) : presets[initialPreset].start();
  const initialEnd = isAllTimeRange ? null : searchParams.get("end") ? dayjs.utc(searchParams.get("end")) : presets[initialPreset].end();
  const initialSkipRdr = searchParams.get("skipRdr") === "true";
  const initialIncludeUnrepresented = searchParams.get("includeUnrepresented") === "true";
  const initialSkipShopifyProtected = searchParams.get("skipShopifyProtected") === "true";
  const initialAutoSubmitted = searchParams.get("autoSubmitted") === "true";
  const initialSearch = searchParams.get("search") || "";
  const initialAgentFilter = splitParam(searchParams.get("userIds"));
  const initialPpAccounts = splitParam(searchParams.get("ppAccounts"));
  const initialDateField = searchParams.get("dateField") ?? "created";
  const initialCaseType = splitParam(searchParams.get("ppCaseTypes"));
  const initialReasonCategory = splitParam(searchParams.get("reasonCategories"));
  // Default to the CM status, not a provider-native one: "needs_response" only exists for
  // Stripe/Shopify, so it hid every PayPal and Adyen dispute. "all" is the explicit cleared state.
  const rawCmStatus = searchParams.get("cmStatuses");
  const initialCmStatus = rawCmStatus === null ? ["open"] : rawCmStatus === "all" ? [] : splitParam(rawCmStatus);
  const initialPpType = splitParam(searchParams.get("ppTypes"));
  const initialCompanyIds = splitParam(searchParams.get("companyIds"));
  const initialReportingCategory = splitParam(searchParams.get("reportingCategories"));

  const [preset, setPreset] = useState<PresetKey>(initialPreset);
  const [startDate, setStartDate] = useState<Dayjs | null>(initialStart);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialEnd);
  const [skipRdr, setSkipRdr] = useState<boolean>(initialSkipRdr);
  const [includeUnrepresented, setIncludeUnrepresented] = useState<boolean>(initialIncludeUnrepresented);
  const [skipShopifyProtected, setSkipShopifyProtected] = useState<boolean>(initialSkipShopifyProtected);
  const [autoSubmitted, setAutoSubmitted] = useState<boolean>(initialAutoSubmitted);
  const [searchQuery, setSearchQuery] = useState<string>(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState<string>(initialSearch);
  const [agentFilter, setAgentFilter] = useState<string[]>(initialAgentFilter);
  const [ppAccountFilter, setPpAccountFilter] = useState<string[]>(initialPpAccounts);
  const [dateField, setDateField] = useState<string>(initialDateField);
  const [caseTypeFilter, setCaseTypeFilter] = useState<string[]>(initialCaseType);
  const [reasonCategoryFilter, setReasonCategoryFilter] = useState<string[]>(initialReasonCategory);
  const [cmStatusFilter, setCmStatusFilter] = useState<string[]>(initialCmStatus);
  const [ppTypeFilter, setPpTypeFilter] = useState<string[]>(initialPpType);
  const [companyIdsFilter, setCompanyIdsFilter] = useState<string[]>(initialCompanyIds);
  const [reportingCategoryFilter, setReportingCategoryFilter] = useState<string[]>(initialReportingCategory);

  const [disputesLoading, setDisputesLoading] = useState(false);
  const [disputesData, setDisputesData] = useState<DisputesResponse | null>(null);
  const [page, setPage] = useState(0);
  const [selectedBucket, setSelectedBucket] = useState<BucketSelection | null>(() => bucketFromParams(searchParams));
  const [selectedCompanyRepresented, setSelectedCompanyRepresented] = useState<boolean | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('evidence_due_by');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // No default any more — the cleared-state "all" is still honoured so existing bookmarks
  // and dashboard deep links that pass it don't filter on a literal "all" status.
  const rawPpStatus = searchParams.get("ppStatuses");
  const initialPpStatusFilter = rawPpStatus === null || rawPpStatus === "all" ? [] : splitParam(rawPpStatus);
  const [ppStatusFilter, setPpStatusFilter] = useState<string[]>(initialPpStatusFilter);

  const [pageError, setPageError] = useState<string | null>(null);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Bulk assign state
  const [selectedDisputeIds, setSelectedDisputeIds] = useState<Set<number>>(new Set());
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [bulkAssignAgent, setBulkAssignAgent] = useState<number | null>(null);
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
  const [bulkAssignError, setBulkAssignError] = useState<string | null>(null);

  // Column configuration
  const [visibleColumns, setVisibleColumns] = useState<string[]>(getStoredColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const columnMenuOpen = Boolean(columnMenuAnchor);

  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleColumnToggle = (columnId: string) => {
    setVisibleColumns(prev => {
      const newColumns = prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId];
      saveStoredColumns(newColumns);
      return newColumns;
    });
  };

  const handleResetColumns = () => {
    setVisibleColumns(defaultColumnIds);
    saveStoredColumns(defaultColumnIds);
  };

  const hasAllCompaniesPermission = useMemo(() => {
    return user?.permissions?.includes(PERMISSIONS.DISPUTES_ALL_COMPANIES) ?? false;
  }, [user?.permissions]);

  // The Representment and Notes columns are agency-only; a persisted (localStorage) selection
  // must not keep rendering them after a permission downgrade, when the picker can no longer hide them.
  const isColumnPermitted = useCallback(
    (columnId: string) => (columnId !== 'representment' && columnId !== 'cm_notes' && columnId !== 'company_name') || hasAllCompaniesPermission,
    [hasAllCompaniesPermission],
  );
  const effectiveVisibleColumns = useMemo(
    () => visibleColumns.filter(isColumnPermitted),
    [visibleColumns, isColumnPermitted],
  );

  const hasWritePermission = useMemo(() => {
    return user?.permissions?.includes(PERMISSIONS.DISPUTE_WRITE) ?? false;
  }, [user?.permissions]);

  const hasAssignPermission = useMemo(() => {
    return user?.permissions?.includes(PERMISSIONS.DISPUTES_ASSIGN) ?? false;
  }, [user?.permissions]);

  const hasCsvPermission = useMemo(() => {
    return user?.permissions?.includes(PERMISSIONS.DISPUTES_CSV) ?? false;
  }, [user?.permissions]);

  const hasClientCsvPermission = useMemo(() => {
    return !hasCsvPermission && (user?.permissions?.includes(PERMISSIONS.DISPUTE_READ) ?? false);
  }, [user?.permissions, hasCsvPermission]);

  const ppAccountOptions = useMemo<PpAccountOption[]>(() => {
    return ppAccounts
      .filter((account) => DISPUTE_PP_TYPES.includes(account.ppType))
      .filter((account) => !companyId || account.companyId === companyId)
      .map((account) => ({
        value: getPpAccountKey(account.ppType, account.ppAccountId),
        label: `${formatPpAccountLabel(account)}${!companyId ? ` (${account.companyName})` : ''}`,
        ppType: account.ppType,
      }));
  }, [ppAccounts, companyId]);

  // Drop selected accounts that are no longer available (e.g. after a company change).
  // Skip while ppAccounts are still loading — otherwise a URL-hydrated filter gets
  // wiped on first render because the options list is empty until the fetch resolves.
  useEffect(() => {
    if (ppAccounts.length === 0) return;
    const valid = ppAccountFilter.filter((v) => ppAccountOptions.some((o) => o.value === v));
    if (valid.length !== ppAccountFilter.length) {
      setPpAccountFilter(valid);
      setPage(0);
    }
  }, [ppAccounts.length, ppAccountOptions, ppAccountFilter]);

  // Fetch agents once on mount (only if has permission)
  useEffect(() => {
    const fetchAgents = async () => {
      if (!hasAllCompaniesPermission) {
        return;
      }
      if (agents.length > 0) return; // Already fetched

      try {
        const response = await get<Agent[]>('/api/users/agents');
        dispatch({ type: 'SET_AGENTS', agents: response });
      } catch (err) {
        console.error('Failed to fetch agents:', err);
        Sentry.captureException(err);
      }
    };
    fetchAgents();
  }, [hasAllCompaniesPermission, agents.length, get, dispatch]);

  // Track whether the selected company is one we represent, to explain an otherwise
  // unexplained empty/thin list when the default hides non-represented companies (agency view).
  useEffect(() => {
    if (!hasAllCompaniesPermission || !companyId) {
      setSelectedCompanyRepresented(null);
      return;
    }
    let cancelled = false;
    // Clear the previous company's flag so a stale value can't flash the wrong banner while loading.
    setSelectedCompanyRepresented(null);
    get<{ representment_enabled: boolean }>(`/api/companies/${companyId}/settings`)
      .then((res) => { if (!cancelled) setSelectedCompanyRepresented(res.representment_enabled); })
      .catch((err) => { if (!cancelled) { setSelectedCompanyRepresented(null); Sentry.captureException(err); } });
    return () => { cancelled = true; };
  }, [hasAllCompaniesPermission, companyId, get]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0); // Reset to first page when search changes
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update URL whenever filter changes (companyId is handled by useCompanyUrlSync in AppLayout)
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    // Both dates cleared = all time; keep the URL round-trippable as range=all.
    params.set("range", !startDate && !endDate ? "all" : preset);
    if (startDate) params.set("start", startDate.format("YYYY-MM-DD")); else params.delete("start");
    if (endDate) params.set("end", endDate.format("YYYY-MM-DD")); else params.delete("end");
    if (ppStatusFilter.length > 0) {
      params.set("ppStatuses", ppStatusFilter.join(","));
    } else {
      params.delete("ppStatuses");
    }
    if (skipRdr) {
      params.set("skipRdr", "true");
    } else {
      params.delete("skipRdr");
    }
    if (includeUnrepresented) {
      params.set("includeUnrepresented", "true");
    } else {
      params.delete("includeUnrepresented");
    }
    if (skipShopifyProtected) {
      params.set("skipShopifyProtected", "true");
    } else {
      params.delete("skipShopifyProtected");
    }
    if (autoSubmitted) {
      params.set("autoSubmitted", "true");
    } else {
      params.delete("autoSubmitted");
    }
    if (debouncedSearch) {
      params.set("search", debouncedSearch);
    } else {
      params.delete("search");
    }
    if (agentFilter.length > 0) {
      params.set("userIds", agentFilter.join(","));
    } else {
      params.delete("userIds");
    }
    if (ppAccountFilter.length > 0) {
      params.set("ppAccounts", ppAccountFilter.join(","));
    } else {
      params.delete("ppAccounts");
    }
    if (dateField && dateField !== "created") {
      params.set("dateField", dateField);
    } else {
      params.delete("dateField");
    }
    if (caseTypeFilter.length > 0) {
      params.set("ppCaseTypes", caseTypeFilter.join(","));
    } else {
      params.delete("ppCaseTypes");
    }
    if (reasonCategoryFilter.length > 0) {
      params.set("reasonCategories", reasonCategoryFilter.join(","));
    } else {
      params.delete("reasonCategories");
    }
    // "all" rather than deleting: an empty CM status is the user explicitly clearing the
    // default, and that has to survive a reload instead of silently re-defaulting to open.
    params.set("cmStatuses", cmStatusFilter.length > 0 ? cmStatusFilter.join(",") : "all");
    if (ppTypeFilter.length > 0) {
      params.set("ppTypes", ppTypeFilter.join(","));
    } else {
      params.delete("ppTypes");
    }
    if (companyIdsFilter.length > 0) {
      params.set("companyIds", companyIdsFilter.join(","));
    } else {
      params.delete("companyIds");
    }
    if (reportingCategoryFilter.length > 0) {
      params.set("reportingCategories", reportingCategoryFilter.join(","));
    } else {
      params.delete("reportingCategories");
    }
    if (selectedBucket) {
      params.set("bucket", BUCKET_PARAM[selectedBucket.bucket]);
    } else {
      params.delete("bucket");
    }
    if (selectedBucket?.unassignedOnly) {
      params.set("unassigned", "1");
    } else {
      params.delete("unassigned");
    }
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, startDate, endDate, ppStatusFilter, skipRdr, includeUnrepresented, skipShopifyProtected, autoSubmitted, debouncedSearch, agentFilter, ppAccountFilter, dateField, caseTypeFilter, reasonCategoryFilter, cmStatusFilter, ppTypeFilter, companyIdsFilter, reportingCategoryFilter, selectedBucket]);

  const fetchDisputes = useCallback(async () => {
    setDisputesLoading(true);
    try {
      const dateFrom = startDate ? startDate.format("YYYY-MM-DD") : '';
      const dateTo = endDate ? endDate.format("YYYY-MM-DD") : '';

      let dateParams: Record<string, string> = {};
      if (dateField === 'evidence_due') {
        dateParams = {
          ...(dateFrom && { evidenceDueFrom: dateFrom }),
          ...(dateTo && { evidenceDueTo: dateTo }),
        };
      } else if (dateField === 'closed') {
        dateParams = {
          ...(dateFrom && { closedFrom: dateFrom }),
          ...(dateTo && { closedTo: dateTo }),
        };
      } else {
        dateParams = {
          ...(dateFrom && { ppCreatedFrom: dateFrom }),
          ...(dateTo && { ppCreatedTo: dateTo }),
        };
      }

      const companyIdsParam = companyIdsFilter.length > 0 ? companyIdsFilter.join(",") : (companyId ? companyId.toString() : '');

      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        sortBy,
        sortOrder,
        ...(ppStatusFilter.length > 0 && { ppStatuses: ppStatusFilter.join(",") }),
        ...dateParams,
        ...(skipRdr && { skipRdr: 'true' }),
        ...(includeUnrepresented && { includeUnrepresented: 'true' }),
        ...(skipShopifyProtected && { skipShopifyProtected: 'true' }),
        ...(autoSubmitted && { autoSubmitted: 'true' }),
        ...(debouncedSearch && { searchTerm: debouncedSearch }),
        ...(agentFilter.length > 0 && { userIds: agentFilter.join(",") }),
        ...(ppAccountFilter.length > 0 && { ppAccounts: ppAccountFilter.join(",") }),
        ...(caseTypeFilter.length > 0 && { ppCaseTypes: caseTypeFilter.join(",") }),
        ...(reasonCategoryFilter.length > 0 && { reasonCategories: reasonCategoryFilter.join(",") }),
        ...(cmStatusFilter.length > 0 && { cmStatuses: cmStatusFilter.join(",") }),
        ...(ppTypeFilter.length > 0 && { ppTypes: ppTypeFilter.join(",") }),
        ...(brandId && { brands: String(brandId) }),
        ...(companyIdsParam && { companyIds: companyIdsParam }),
        ...(reportingCategoryFilter.length > 0 && { reportingCategories: reportingCategoryFilter.join(",") }),
        ...(selectedBucket && { bucket: BUCKET_PARAM[selectedBucket.bucket] }),
        ...(selectedBucket?.unassignedOnly && { unassignedOnly: 'true' }),
      });

      const response = await get<DisputesResponse>(`/api/disputes/?${params}`);
      setDisputesData(response);
    } catch (err) {
      console.error('Failed to fetch disputes:', err);
      setPageError(err instanceof Error ? err.message : 'Unknown error');
      Sentry.captureException(err);
    } finally {
      setDisputesLoading(false);
    }
  }, [page, rowsPerPage, sortBy, sortOrder, ppStatusFilter, startDate, endDate, companyId, skipRdr, includeUnrepresented, skipShopifyProtected, autoSubmitted, debouncedSearch, agentFilter, ppAccountFilter, dateField, caseTypeFilter, reasonCategoryFilter, cmStatusFilter, ppTypeFilter, brandId, companyIdsFilter, reportingCategoryFilter, selectedBucket, get]);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      setPage(0);
    } else {
      setSortBy(field);
      setSortOrder('asc');
      setPage(0);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePresetChange = (newPreset: PresetKey) => {
    setPreset(newPreset);
    if (newPreset !== "custom") {
      setStartDate(presets[newPreset].start());
      setEndDate(presets[newPreset].end());
    }
  };

  const handleStartDateChange = (newValue: Dayjs | null) => {
    setStartDate(newValue);
    setPreset("custom");
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    setEndDate(newValue);
    setPreset("custom");
  };

  const handleBucketSelect = (selection: BucketSelection) => {
    setPage(0);
    if (selectedBucket?.bucket === selection.bucket && selectedBucket.unassignedOnly === selection.unassignedOnly) {
      // Deselect only removes the bucket narrowing; filters stay as they are.
      setSelectedBucket(null);
      return;
    }
    setSelectedBucket(selection);
    // The cards count over a fixed 90-day window with no other filters — reset the
    // page to that exact state so the table shows precisely the counted disputes.
    setPreset('90D');
    setStartDate(presets['90D'].start());
    setEndDate(presets['90D'].end());
    setDateField('created');
    setPpStatusFilter([]);
    setSkipRdr(false);
    setIncludeUnrepresented(false);
    setSkipShopifyProtected(false);
    setAutoSubmitted(false);
    setSearchQuery('');
    setDebouncedSearch('');
    setAgentFilter([]);
    setPpAccountFilter([]);
    setCaseTypeFilter([]);
    setReasonCategoryFilter([]);
    setCmStatusFilter([]);
    setPpTypeFilter([]);
    setReportingCategoryFilter([]);
  };

  const handleStatusChange = (newStatus: string[]) => {
    setPpStatusFilter(newStatus);
  };

  const handleAgentChange = (agentIds: string[]) => {
    setAgentFilter(agentIds);
    setPage(0);
  };

  const handlePpAccountsChange = (ppAccounts: string[]) => {
    setPpAccountFilter(ppAccounts);
    setPage(0);
  };

  const handleDateFieldChange = (newDateField: string) => {
    setDateField(newDateField);
    setPage(0);
  };

  const dateFieldOptions = [
    { value: 'created', label: 'Created' },
    { value: 'evidence_due', label: 'Due' },
    { value: 'closed', label: 'Closed' },
  ];

  const caseTypeOptions = [
    { value: 'INQUIRY', label: pascalCase('INQUIRY') },
    { value: 'CHARGEBACK', label: pascalCase('CHARGEBACK') },
    { value: 'PRE_ARBITRATION', label: pascalCase('PRE_ARBITRATION') },
  ];

  const handleCaseTypeChange = (caseType: string[]) => {
    setCaseTypeFilter(caseType);
    setPage(0);
  };

  const reasonCategoryOptions = [
    { value: 'fraudulent', label: 'Fraudulent' },
    { value: 'product_not_received', label: 'Product Not Received' },
    { value: 'product_unacceptable', label: 'Product Unacceptable' },
    { value: 'duplicate', label: 'Duplicate' },
    { value: 'subscription_canceled', label: 'Subscription Canceled' },
    { value: 'credit_not_processed', label: 'Credit Not Processed' },
    { value: 'general', label: 'General' },
    { value: 'other', label: 'Other' },
  ];

  // PayPal-only reporting categories (values match disputes_paypal.reporting_category).
  const reportingCategoryOptions = [
    { value: 'Dispute', label: 'Inquiry' },
    { value: 'Claim', label: 'Claim' },
    { value: 'Unauthorized Claim', label: 'Unauthorized Claim' },
    { value: 'Bank Return', label: 'Bank Return' },
    { value: 'Card Chargeback', label: 'Card Chargeback' },
    { value: 'Pre-chargeback Alert', label: 'Pre-chargeback Alert' },
    { value: 'Unknown', label: 'Unknown' },
  ];

  const handleReportingCategoryChange = (reportingCategory: string[]) => {
    setReportingCategoryFilter(reportingCategory);
    setPage(0);
  };

  const handleReasonCategoryChange = (reasonCategory: string[]) => {
    setReasonCategoryFilter(reasonCategory);
    setPage(0);
  };

  const handleCmStatusChange = (cmStatus: string[]) => {
    setCmStatusFilter(cmStatus);
    setPage(0);
  };

  const ppTypeOptions = DISPUTE_PP_TYPES.map(value => ({ value, label: pascalCase(value) }));

  const handlePpTypeChange = (ppType: string[]) => {
    setPpTypeFilter(ppType);
    setPage(0);
  };

  const companyOptions = miniCompanies.map((c) => ({ value: String(c.id), label: c.name, searchText: companySearchText(c) }));

  const handleCompanyIdsChange = (companyIds: string[]) => {
    setCompanyIdsFilter(companyIds);
    setPage(0);
  };

  // Bulk assign selection helpers
  const selectableDisputes = useMemo(
    () => disputesData?.disputes.filter(isDisputeAssignable) ?? [],
    [disputesData?.disputes]
  );
  const allOnPageSelected = selectableDisputes.length > 0 && selectableDisputes.every(d => selectedDisputeIds.has(d.dispute_id));
  const someOnPageSelected = selectableDisputes.some(d => selectedDisputeIds.has(d.dispute_id)) && !allOnPageSelected;

  const handleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedDisputeIds(prev => {
        const next = new Set(prev);
        selectableDisputes.forEach(d => next.delete(d.dispute_id));
        return next;
      });
    } else {
      setSelectedDisputeIds(prev => {
        const next = new Set(prev);
        selectableDisputes.forEach(d => next.add(d.dispute_id));
        return next;
      });
    }
  };

  const handleToggleSelect = (disputeId: number) => {
    setSelectedDisputeIds(prev => {
      const next = new Set(prev);
      if (next.has(disputeId)) next.delete(disputeId);
      else next.add(disputeId);
      return next;
    });
  };

  const clearSelection = () => setSelectedDisputeIds(new Set());

  // Clear selection when filters change
  useEffect(() => {
    clearSelection();
  }, [ppStatusFilter, startDate, endDate, companyId, skipRdr, skipShopifyProtected, autoSubmitted, debouncedSearch, agentFilter, ppAccountFilter, dateField, caseTypeFilter, reasonCategoryFilter, cmStatusFilter]);

  const handleBulkAssign = async () => {
    if (!bulkAssignAgent) return;
    setBulkAssignLoading(true);
    setBulkAssignError(null);
    try {
      const count = selectedDisputeIds.size;
      await post('/api/disputes/assign', {
        disputeIds: [...selectedDisputeIds],
        userId: bulkAssignAgent,
      });
      await fetchDisputes();
      clearSelection();
      setBulkAssignDialogOpen(false);
      setBulkAssignAgent(null);
      const agent = agents.find(a => a.userId === bulkAssignAgent);
      setSnackbar({ open: true, message: `${count} dispute${count !== 1 ? 's' : ''} assigned to ${agent?.name ?? 'user'}`, severity: 'success' });
    } catch (err) {
      setBulkAssignError(err instanceof Error ? err.message : 'Failed to assign disputes');
      Sentry.captureException(err);
    } finally {
      setBulkAssignLoading(false);
    }
  };

  const handleCloseBulkAssignDialog = () => {
    setBulkAssignDialogOpen(false);
    setBulkAssignAgent(null);
    setBulkAssignError(null);
  };

  const alreadyAssignedCount = useMemo(() => {
    if (!disputesData) return 0;
    return disputesData.disputes.filter(
      d => selectedDisputeIds.has(d.dispute_id) && d.assigned_user_id
    ).length;
  }, [disputesData, selectedDisputeIds]);

  const hasSelection = selectedDisputeIds.size > 0 && hasAssignPermission;

  return (
    <div>
      <PageMeta
        title="Chargemont | Disputes"
        description="Chargemont | Disputes"
      />
      <PageContainer>
        <PageHeader
          title="Disputes"
          action={
            (hasCsvPermission || hasClientCsvPermission) && (
              <Button
                variant="contained"
                onClick={() => setCsvDialogOpen(true)}
                sx={{
                  backgroundColor: '#465fff',
                  '&:hover': {
                    backgroundColor: '#3641f5',
                  },
                }}
              >
                Export CSV
              </Button>
            )
          }
        />

        {hasAssignPermission && disputesData?.summary && (
          <DisputeSummaryCards
            summary={disputesData.summary}
            serverNow={disputesData.serverNow}
            selected={selectedBucket}
            onSelect={handleBucketSelect}
          />
        )}

        {/* Filters */}
        <Box className="mb-6">
          <DataFilter
            preset={preset}
            startDate={startDate}
            endDate={endDate}
            onPresetChange={handlePresetChange}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            statusOptions={statusOptions}
            status={ppStatusFilter}
            onStatusChange={handleStatusChange}
            cmStatusOptions={cmStatusOptions}
            cmStatus={cmStatusFilter}
            onCmStatusChange={handleCmStatusChange}
            ppTypeOptions={ppTypeOptions}
            ppType={ppTypeFilter}
            onPpTypeChange={handlePpTypeChange}
            companyOptions={hasAllCompaniesPermission ? companyOptions : undefined}
            companyIds={companyIdsFilter}
            onCompanyIdsChange={hasAllCompaniesPermission ? handleCompanyIdsChange : undefined}
            skipRdr={skipRdr}
            onSkipRdrChange={setSkipRdr}
            includeUnrepresented={hasAllCompaniesPermission ? includeUnrepresented : undefined}
            onIncludeUnrepresentedChange={hasAllCompaniesPermission ? setIncludeUnrepresented : undefined}
            skipShopifyProtected={skipShopifyProtected}
            onSkipShopifyProtectedChange={setSkipShopifyProtected}
            autoSubmitted={hasAllCompaniesPermission ? autoSubmitted : undefined}
            onAutoSubmittedChange={hasAllCompaniesPermission ? setAutoSubmitted : undefined}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search disputes..."
            ppAccountOptions={ppAccountOptions}
            ppAccounts={ppAccountFilter}
            onPpAccountsChange={handlePpAccountsChange}
            agents={agents}
            selectedAgentIds={agentFilter}
            onAgentChange={handleAgentChange}
            dateFieldOptions={dateFieldOptions}
            dateField={dateField}
            onDateFieldChange={handleDateFieldChange}
            caseTypeOptions={caseTypeOptions}
            caseType={caseTypeFilter}
            onCaseTypeChange={handleCaseTypeChange}
            reasonCategoryOptions={reasonCategoryOptions}
            reasonCategory={reasonCategoryFilter}
            onReasonCategoryChange={handleReasonCategoryChange}
            reportingCategoryOptions={reportingCategoryOptions}
            reportingCategory={reportingCategoryFilter}
            onReportingCategoryChange={handleReportingCategoryChange}
          />
        </Box>

        {/* Representment-off notice: explains why a selected company's disputes are hidden */}
        {hasAllCompaniesPermission && companyId && selectedCompanyRepresented === false && !includeUnrepresented && (
          <Alert severity="info" sx={{ mb: 2 }}>
            We don&apos;t do representment for this company, so its disputes are hidden by default.
            Untick <strong>&quot;only represented companies&quot;</strong> in the filters to show them.
          </Alert>
        )}

        {/* Error State */}
        {(apiError || pageError) && (
          <Box className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            Error: {apiError || pageError}
          </Box>
        )}

        {/* Loading State */}
        {disputesLoading && (
          <Box className="flex justify-center items-center py-8">
            <CircularProgress size={64} />
          </Box>
        )}

        {/* MUI Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ minWidth: '100%', '& td, & th': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow sx={hasSelection ? { backgroundColor: '#EAF6FF' } : undefined}>
                  {hasAssignPermission && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={someOnPageSelected}
                        checked={allOnPageSelected}
                        onChange={handleSelectAll}
                        size="small"
                      />
                    </TableCell>
                  )}
                  {hasSelection ? (
                    <TableCell colSpan={100}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedDisputeIds.size} dispute{selectedDisputeIds.size !== 1 ? 's' : ''} selected
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setBulkAssignDialogOpen(true)}
                          sx={{ backgroundColor: '#465fff', '&:hover': { backgroundColor: '#3641f5' }, textTransform: 'none' }}
                        >
                          Assign selected
                        </Button>
                        <Button size="small" color="inherit" onClick={clearSelection} sx={{ textTransform: 'none' }}>
                          Clear
                        </Button>
                      </Stack>
                    </TableCell>
                  ) : (
                    <>
                      <TableCell></TableCell>
                      {allColumns.filter(c => effectiveVisibleColumns.includes(c.id)).map(column => {
                        if (column.sortField) {
                          return (
                            <TableCell key={column.id}>
                              <TableSortLabel
                                active={sortBy === column.sortField}
                                direction={sortBy === column.sortField ? sortOrder : 'asc'}
                                onClick={() => handleSort(column.sortField!)}
                              >
                                {column.label}
                              </TableSortLabel>
                            </TableCell>
                          );
                        }
                        return <TableCell key={column.id}>{column.label}</TableCell>;
                      })}
                      <HasPermission permission={PERMISSIONS.DISPUTES_ALL_COMPANIES}>
                        <TableCell>Assigned To</TableCell>
                      </HasPermission>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <IconButton
                          onClick={handleColumnMenuOpen}
                          size="small"
                          sx={{
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          <SettingsOutlinedIcon fontSize="medium" />
                        </IconButton>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {disputesData?.disputes.map((dispute) => (
                  <DisputeRow
                    key={dispute.dispute_id}
                    dispute={dispute}
                    visibleColumns={effectiveVisibleColumns}
                    isSelected={selectedDisputeIds.has(dispute.dispute_id)}
                    onToggleSelect={handleToggleSelect}
                    selectionEnabled={hasAssignPermission}
                    showActivity={hasAllCompaniesPermission}
                    canWrite={hasWritePermission}
                    canManageNotes={hasAllCompaniesPermission}
                    isAgencyView={hasAllCompaniesPermission}
                    serverNow={disputesData?.serverNow}
                    onRefreshed={fetchDisputes}
                    onNotify={(message, severity) => setSnackbar({ open: true, message, severity })}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {disputesData && (
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={disputesData.pagination.totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>

        {/* Empty State */}
        {disputesData && disputesData.disputes.length === 0 && !disputesLoading && (
          <Box className="text-center py-8">
            <Typography variant="body1" color="text.secondary">
              No disputes found.
            </Typography>
          </Box>
        )}
      </PageContainer>

      {/* CSV Export Dialog */}
      <DisputesCsvDialog
        open={csvDialogOpen}
        onClose={() => setCsvDialogOpen(false)}
        clientMode={hasClientCsvPermission}
      />

      {/* Bulk Assign Dialog */}
      <Dialog
        open={bulkAssignDialogOpen}
        onClose={handleCloseBulkAssignDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Disputes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Assign {selectedDisputeIds.size} dispute{selectedDisputeIds.size !== 1 ? 's' : ''} to:
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="bulk-assign-agent-label">Select User</InputLabel>
            <Select
              labelId="bulk-assign-agent-label"
              value={bulkAssignAgent ?? ''}
              label="Select User"
              onChange={(e) => setBulkAssignAgent(e.target.value ? Number(e.target.value) : null)}
            >
              {agents.map((agent) => (
                <MenuItem key={agent.userId} value={agent.userId}>
                  {agent.name} ({agent.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {alreadyAssignedCount > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {alreadyAssignedCount} of {selectedDisputeIds.size} dispute{selectedDisputeIds.size !== 1 ? 's' : ''} {alreadyAssignedCount === 1 ? 'is' : 'are'} already assigned and will be reassigned.
            </Alert>
          )}
          {bulkAssignError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {bulkAssignError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseBulkAssignDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleBulkAssign}
            variant="contained"
            disabled={!bulkAssignAgent || bulkAssignLoading}
            sx={{ backgroundColor: '#465fff', '&:hover': { backgroundColor: '#3641f5' } }}
          >
            {bulkAssignLoading ? <CircularProgress size={20} color="inherit" /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Column Settings Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={columnMenuOpen}
        onClose={handleColumnMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 400,
              width: 250,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Configure Columns
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 1 }}>
          {allColumns.filter((column) => isColumnPermitted(column.id)).map((column) => (
            <MenuItem
              key={column.id}
              dense
              sx={{ py: 0.5 }}
              onClick={() => handleColumnToggle(column.id)}
            >
              <Checkbox
                checked={visibleColumns.includes(column.id)}
                size="small"
                sx={{ py: 0, mr: 1 }}
              />
              <Typography variant="body2">{column.label}</Typography>
            </MenuItem>
          ))}
        </Box>
        <Divider />
        <MenuItem onClick={handleResetColumns} dense>
          <Typography variant="body2" color="primary">
            Reset to Default
          </Typography>
        </MenuItem>
      </Menu>
    </div>
  );
}

